import { Box, Paper, Typography } from "@mui/material";
import { tooltipShadow } from "~/themes/light/tokens";
import { BoomStatCardProps } from "./types";

export function BoomStatCard(props: BoomStatCardProps) {
  const { label, variant } = props;

  let displayValue: string | number;
  let isBarVisible: boolean;
  let barSegments: Array<{ percentage: number; color: string }> = [];

  switch (variant) {
    case "percentage":
      displayValue = `${props.percentage}%`;
      isBarVisible = true;
      barSegments = [
        {
          percentage: Math.min(Math.max(props.percentage, 0), 100),
          color: props.color,
        },
      ];
      break;

    case "simple":
      displayValue = props.value.toLocaleString();
      isBarVisible = false;
      break;

    case "stacked":
      const total = props.segments.reduce((sum, seg) => sum + seg.value, 0);
      displayValue = total.toLocaleString();
      isBarVisible = true;
      barSegments = props.segments.map((seg) => ({
        percentage: total > 0 ? (seg.value / total) * 100 : 0,
        color: seg.color,
      }));
      break;
  }

  const segmentOffsets = barSegments.reduce<number[]>(
    (acc, segment) => [...acc, (acc.at(-1) ?? 0) + segment.percentage],
    [0],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 0,
        minWidth: "270px",
        height: "90px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        backgroundColor: "background.paper",
        boxShadow: tooltipShadow,
      }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 1.8 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: "32px",
            color: "text.primary",
          }}
        >
          {displayValue}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "24px",
            fontWeight: 400,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </Typography>
      </Box>
      {/* Progress Bar (only shown for percentage and stacked variants) */}
      {isBarVisible && (
        <Box
          sx={{
            position: "relative",
            height: "4px",
            width: "100%",
            borderBottomLeftRadius: 3,
            borderBottomRightRadius: 3,
            overflow: "hidden",
          }}
        >
          {barSegments.map((segment, index) => (
            <Box
              key={index}
              sx={{
                position: "absolute",
                left: `${segmentOffsets[index]}%`,
                top: 0,
                height: "100%",
                width: `${segment.percentage}%`,
                backgroundColor: segment.color,
                ...(index === 0 && {
                  borderTopLeftRadius: 1,
                  borderBottomLeftRadius: 1,
                }),
                ...(index === barSegments.length - 1 && {
                  borderTopRightRadius: 1,
                  borderBottomRightRadius: 1,
                }),
              }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
}
